import React from 'react'
import { FormTextField, FormPlaceSuggest, FormDateSuggest } from '@fs/zion-form'
import zionDebug from '@fs/zion-debug'
import {
  countSupersOfSharedField,
  isDateField,
  isEditableField,
  isPlaceField,
  shouldLockSharedField,
  useSlsStateContext,
} from '@fs/zion-sls'
import EditSharedFieldButton from './EditSharedFieldButton'
import EditDanglingFieldButton from './EditDanglingFieldButton'
import { handleOnFocus, handleFieldChange } from './utils/editPanelFieldHelpers'
import { useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import HighlightButtonOld from '../../../../components/generic/panels/shared/HighlightButtonOld'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'

const debug = zionDebug('rip:EditPersonFormElement')

export default function EditPersonFormElement({ field, showEditHighlight = true, enableLockSharedField = true }) {
  const r2Dispatch = useR2DispatchContext()
  const state = useSlsStateContext()
  // Turn off the isShared bool if the flag is on so we can test the dangling field edit feature.  Story https://www5.v1host.com/FH-V1/story.mvc/Summary?oidToken=Story:1977049

  const { restrictedFields } = useCollectionContextProvider()
  const isRestrictedNameField = restrictedFields?.includes(FIELDS.NAME)
  const isRestrictedRaceField = restrictedFields?.includes(FIELDS.RACE)

  const numberOfSharedSupers = countSupersOfSharedField(field, state.images)
  const isShared = enableLockSharedField && numberOfSharedSupers > 1 // This is simpler than looking at the field.superElements because there are floating NAME elements left over from when persons were marked vacant that shouldn't be counted.
  const isDangling = field.isDangling
  const isEditable = isEditableField(field)
  debug({
    field,
    fieldType: field?.fieldType,
    isEditable,
    numberOfSharedSupers,
  })

  const checkIsAllowedToEdit = () => {
    if (field.fieldType.includes('NAME')) return isRestrictedNameField
    if (field.fieldType.includes('RACE')) return isRestrictedRaceField
    return false
  }

  return (
    <>
      {!isShared && isDangling && <EditDanglingFieldButton field={field} />}
      {/* at least shared surname uses this */}
      {isShared && (
        <EditSharedFieldButton field={field} readonly={shouldLockSharedField(field, state.images) || !isEditable} />
      )}
      {/* at least normal name fields use this */}
      {!isShared && !isDangling && (
        <>
          {isPlaceField(field) && (
            <FormPlaceSuggest
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              standardize
              onFocus={() => {
                handleOnFocus({ element: field, r2Dispatch })
              }}
              onBlur={() => handleFieldChange(r2Dispatch)}
              clearOnClickAway
            />
          )}
          {isDateField(field) && (
            <FormDateSuggest
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              standardize
              onFocus={() => {
                handleOnFocus({ element: field, r2Dispatch })
              }}
              onBlur={() => handleFieldChange(r2Dispatch)}
              clearOnClickAway
            />
          )}
          {!isPlaceField(field) && !isDateField(field) && (
            <FormTextField
              data-testid={field?.fieldType || field?.elementType}
              name={field.id}
              key={field.id}
              label={field.label}
              placeholder={field.label}
              maxLength={256}
              disabled={checkIsAllowedToEdit()}
              onFocus={() => {
                handleOnFocus({ element: field, r2Dispatch })
              }}
              onBlur={() => handleFieldChange(r2Dispatch)}
            />
          )}
          {showEditHighlight && <HighlightButtonOld field={field} />}
        </>
      )}
    </>
  )
}
